import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { FieldError } from "react-hook-form";
import { Location } from "../models/Location";
import { getLocations } from "../repositories/LocationRepository";

type useLocationsProps = {
	onError: (error: Error) => void;
	onlyStands?: boolean;
	locationId?: number;
};

const useLocations = ({
	onError,
	onlyStands,
	locationId,
}: useLocationsProps) => {
	return useQuery<Location[], Error>({
		queryKey: ["locations", locationId, onlyStands],
		queryFn: ({ signal }) => getLocations({ locationId, onlyStands }, signal),
		onError: onError,
	});
};

type Props = {
	label: string;
	value: number | null;
	onChange?: Function;
	disabled?: boolean;
	onlyStands?: boolean;
	fieldError?: FieldError | undefined;
	helperText?: String;
};

export const LocationSelector = ({
	label,
	value,
	onChange = () => {},
	disabled = false,
	onlyStands = false,
	fieldError,
	helperText,
}: Props) => {
	const [error, setError] = useState("");

	const { data: locations, isLoading } = useLocations({
		onError: (error) => setError(error.message),
		onlyStands: onlyStands,
	});

	const selectedValue =
		locations?.find((location) => location.LocationId === value) || null;

	return (
		<>
			<Autocomplete
				clearOnEscape
				autoHighlight
				options={locations || []}
				getOptionLabel={(option) => option.LocationName}
				disabled={disabled}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						variant="outlined"
						fullWidth
						size="small"
						error={!!fieldError}
						helperText={fieldError?.message || helperText}
						sx={{
							backgroundColor: (theme) => theme.palette.background.paper,
						}}
						FormHelperTextProps={{
							sx: { color: "secondary.main" },
						}}
						inputProps={{
							...params.inputProps,
							"data-test-id": onlyStands
								? "stand-selector"
								: "location-selector",
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
				value={selectedValue}
				onChange={(event, newValue) => {
					onChange(newValue?.LocationId ?? null);
				}}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading guides"
			/>
		</>
	);
};
